import { ref, computed } from 'vue'
import { io, Socket } from 'socket.io-client'
import { useHelpers } from './useHelpers'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

// Singleton instance across the app
let socketInstance: Socket | null = null
const isConnected = ref(false)
const hasSetupConnection = ref(false)
const reconnectAttempts = ref(0)
const lastError = ref<string | null>(null)

function createSocket(): Socket {
  const router = useRouter()
  const { printEvent, getCookie } = useHelpers()
  const { isReconnecting, isNetworkAbsent } = storeToRefs(useAppStore())

  const { VITE_SOCKET_BASE_URL } = import.meta.env

  const accessToken = getCookie('access_token') || ''
  const baseURL = VITE_SOCKET_BASE_URL
  const gameNameSpace = `${baseURL}/game`

  const s = io(gameNameSpace, {
    upgrade: true,
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    autoConnect: true,
    transports: ['websocket'],
    auth: {
      token: accessToken
    }
  })

  s.on('connect', () => {
    hasSetupConnection.value = true
    isConnected.value = true
    isReconnecting.value = false
    reconnectAttempts.value = 0
    lastError.value = null
    printEvent('[socket] connected', s.id, 'debug')
  })

  s.on('disconnect', reason => {
    isConnected.value = false
    printEvent('[socket] connected', reason, 'warn')
    // Socket.IO will auto-reconnect unless forced-close; mark as reconnecting until next state
    isReconnecting.value = true
  })

  s.on('connect_error', error => {
    if (error.message?.toLowerCase().includes('token')) {
      router.push('/logout')
    }
    hasSetupConnection.value = true
    isConnected.value = false
    lastError.value = error?.message || String(error)
    printEvent('[socket] connect_error:', lastError.value, 'error')
    isReconnecting.value = true
  })

  s.on('error', error => {
    lastError.value = (error as any)?.message || String(error)
    printEvent('[socket] error:', lastError.value, 'error')
  })

  // Reconnection lifecycle events
  s.io.on('reconnect_attempt', (attempt: number) => {
    isReconnecting.value = true
    reconnectAttempts.value = attempt
  })
  s.io.on('reconnect', (attempt: number) => {
    isReconnecting.value = false
    reconnectAttempts.value = attempt
    lastError.value = null
  })
  s.io.on('reconnect_error', (err: Error) => {
    lastError.value = err?.message || String(err)
    isNetworkAbsent.value = true
  })
  s.io.on('reconnect_failed', () => {
    isReconnecting.value = false
    lastError.value = 'Reconnection failed'
    isNetworkAbsent.value = true
  })

  return s
}

const useSocket = () => {
  const { printEvent } = useHelpers()
  const { isReconnecting, isNetworkAbsent } = storeToRefs(useAppStore())

  if (!socketInstance) {
    socketInstance = createSocket()
  }

  const socket = computed(() => socketInstance)

  // Promise-based emit with ack + timeout
  const sendEventAsync = async <TResponse = unknown, TPayload = Record<any, any>>(eventName: string, data: TPayload = {} as TPayload): Promise<SocketResponseType<TResponse>> => {
    return new Promise<SocketResponseType<TResponse>>((resolve, reject) => {
      if (!socketInstance) return reject(new Error('Socket not initialized'))
      socketInstance.emit(eventName, data as any, (response: SocketResponseType<TResponse> | null) => {
        if (response?.error) {
          return reject(new Error(response.error))
        }
        resolve(response as SocketResponseType<TResponse>)
      })
    })
  }

  const emit = <T = unknown>(eventName: string, data?: T) => {
    if (!socketInstance) throw new Error('Socket not initialized')
    socketInstance.emit(eventName, data as T, (err: SocketResponseType | null) => {
      if (err) {
        printEvent(`[socket] emit error for ${eventName}:`, err.error, 'error')
        lastError.value = err.error
      } else {
        lastError.value = null
      }
    })
  }

  const on = <T = any>(event: string, callback: (payload: T) => void) => {
    socketInstance?.on(event, callback as any)
  }

  const off = (event: string, callback?: (...args: any[]) => void) => {
    socketInstance?.off(event, callback as any)
  }

  const once = <T = any>(event: string, callback: (payload: T) => void) => {
    socketInstance?.once(event, callback as any)
  }

  const disconnect = () => {
    socketInstance?.disconnect()
  }

  const connect = () => {
    printEvent('[socket] connecting...', 'log')

    if (socketInstance && !socketInstance.connected) {
      lastError.value = null
      isReconnecting.value = true
      isNetworkAbsent.value = false
      socketInstance.connect()
    }
  }

  // // Optional: clean up listeners on component unmount if user adds any
  // onUnmounted(() => {
  //   // Keep the connection singleton alive; only remove listeners if needed
  // })

  const onMessage = (callback: (message: string) => void) => on('message', callback)
  const onGameStateChange = (callback: (state: string) => void) => on('GameStateChanged', callback)

  return {
    hasSetupConnection,
    socket,
    isConnected,
    isReconnecting,
    reconnectAttempts,
    lastError,
    emit,
    on,
    off,
    once,
    disconnect,
    connect,
    sendEventAsync,
    onMessage,
    onGameStateChange
  }
}

export default useSocket
